import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Fab,
  Snackbar,
  Stack,
  SwipeableDrawer,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import {
  adminOrderService,
  inventoryService,
  locationService,
  purchaseCatalogService,
  storeLocationMappingService,
} from "../services";
import { formatOrganizationLabel } from "../utils/organizationLabel";
import { resolveResultToCatalogRow } from "../utils/purchaseOrderResolve";
import { useOrganizationInfiniteList } from "../hooks/useOrganizationInfiniteList";
import { usePurchaseCatalogQuery } from "../hooks/usePurchaseCatalogQuery";
import { useCreatePurchaseOrderCart } from "../hooks/useCreatePurchaseOrderCart";
import { useCartNavigationBlocker } from "../hooks/useCartNavigationBlocker";
import PurchaseOrderHeader from "../components/create-purchase-order/PurchaseOrderHeader";
import ProductSearchBar from "../components/create-purchase-order/ProductSearchBar";
import ProductFilters from "../components/create-purchase-order/ProductFilters";
import ProductList from "../components/create-purchase-order/ProductList";
import SelectedProductsPanel from "../components/create-purchase-order/SelectedProductsPanel";
import ResponsiveLayout from "../components/create-purchase-order/ResponsiveLayout";
import PriceConfigDialog from "../components/create-purchase-order/PriceConfigDialog";
import PurchaseOrderPriceSelectDialog from "../components/create-purchase-order/PurchaseOrderPriceSelectDialog";
import PurchaseOrderRemediateActions from "../components/create-purchase-order/PurchaseOrderRemediateActions";

const DEFAULT_LOCATION_CODE = "DK11";

async function resolveDefaultPricingLocationForOrg(orgId) {
  if (!orgId) return DEFAULT_LOCATION_CODE;
  try {
    const linksRes = await storeLocationMappingService.getOrgLocationLinksAll(orgId);
    const links = linksRes?.data || linksRes?.items || linksRes || [];
    const linkList = Array.isArray(links) ? links : [];
    const firstLoc = linkList.flatMap((row) => row.locations || [])[0];
    if (firstLoc?.code) {
      return String(firstLoc.code).trim().toUpperCase();
    }
  } catch {
    // fall through to default
  }
  return DEFAULT_LOCATION_CODE;
}

async function ensureOrgMappedToLocationCode(orgId, locationCode) {
  const code = String(locationCode || DEFAULT_LOCATION_CODE).trim();
  const locRes = await locationService.getLocations({ search: code, limit: 50 });
  const locations = locRes?.data || locRes?.items || locRes || [];
  const list = Array.isArray(locations) ? locations : [];
  const location = list.find(
    (l) => String(l.code || "").toUpperCase() === code.toUpperCase(),
  );
  if (!location?.id) return;

  const linksRes = await storeLocationMappingService.getOrgLocationLinksAll(orgId);
  const links = linksRes?.data || linksRes?.items || linksRes || [];
  const linkList = Array.isArray(links) ? links : [];
  const mappedIds = new Set(
    linkList.flatMap((row) =>
      (row.locations || []).map((loc) => loc.id || loc.location_id),
    ),
  );
  if (mappedIds.has(location.id)) return;

  await storeLocationMappingService.create({
    organization_id: orgId,
    location_ids: [location.id],
  });
}

const CreatePurchaseOrderPage = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [selectedOrg, setSelectedOrg] = useState(null);
  const [orgInputValue, setOrgInputValue] = useState("");
  const {
    organizations: orgOptionsBase,
    loading: orgsLoading,
    loadingMore: orgsLoadingMore,
    error: orgListError,
    setSearchText: setOrgSearchText,
    resetSearch: resetOrgSearch,
    handleListboxScroll,
  } = useOrganizationInfiniteList({ pageSize: 20 });

  const organizationOptions = useMemo(() => {
    if (!selectedOrg) return orgOptionsBase;
    if (orgOptionsBase.some((o) => o.id === selectedOrg.id)) {
      return orgOptionsBase;
    }
    return [selectedOrg, ...orgOptionsBase];
  }, [orgOptionsBase, selectedOrg]);

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [cartMessage, setCartMessage] = useState(null);
  const [mobileCartOpen, setMobileCartOpen] = useState(false);
  const [skuLookup, setSkuLookup] = useState("");
  const [skuLookupLoading, setSkuLookupLoading] = useState(false);
  const [skuResolveError, setSkuResolveError] = useState(null);
  const [skuRemediation, setSkuRemediation] = useState(null);
  const [priceDialogProduct, setPriceDialogProduct] = useState(null);
  const [defaultPricingLocationCode, setDefaultPricingLocationCode] =
    useState(DEFAULT_LOCATION_CODE);
  const [priceDialogSaving, setPriceDialogSaving] = useState(false);
  const [priceDialogError, setPriceDialogError] = useState(null);
  const [pendingAddQty, setPendingAddQty] = useState(1);
  const [priceOverrides, setPriceOverrides] = useState({});
  const [priceSelectProduct, setPriceSelectProduct] = useState(null);

  const catalog = usePurchaseCatalogQuery(selectedOrg?.id);

  useEffect(() => {
    if (!selectedOrg?.id) {
      setDefaultPricingLocationCode(DEFAULT_LOCATION_CODE);
      return;
    }
    let cancelled = false;
    resolveDefaultPricingLocationForOrg(selectedOrg.id).then((code) => {
      if (!cancelled) setDefaultPricingLocationCode(code);
    });
    return () => {
      cancelled = true;
    };
  }, [selectedOrg?.id]);

  const {
    lines: cartLines,
    lineCount,
    estimatedSubtotal,
    stockWarnings,
    fulfillmentWarnings,
    addOrUpdateFromCatalogRow,
    updateLineQty,
    updateLinePricing,
    removeLine,
    clearCart,
    getLineForLot,
    toSubmitItems,
  } = useCreatePurchaseOrderCart();
  const { resetFilters, refetch: refetchCatalog } = catalog;

  const hasCartItems = cartLines.length > 0;
  const {
    showLeaveDialog,
    confirmLeave,
    cancelLeave,
    allowNavigation,
    tryNavigate,
  } = useCartNavigationBlocker(hasCartItems);

  const handleOrgChange = useCallback(
    (_, v) => {
      setSelectedOrg(v);
      clearCart();
      setPriceOverrides({});
      resetFilters();
      if (v) {
        const label = formatOrganizationLabel(v);
        setOrgInputValue(label);
        resetOrgSearch();
      } else {
        setOrgInputValue("");
        resetOrgSearch();
      }
    },
    [clearCart, resetFilters, resetOrgSearch],
  );

  const handleOrgInputChange = useCallback(
    (_, value, reason) => {
      if (reason === "input") {
        setOrgInputValue(value);
        setOrgSearchText(value);
        if (selectedOrg) {
          const selectedLabel = formatOrganizationLabel(selectedOrg);
          if (value !== selectedLabel) {
            setSelectedOrg(null);
          }
        }
      } else if (reason === "clear") {
        setOrgInputValue("");
        resetOrgSearch();
        setSelectedOrg(null);
      } else if (reason === "reset") {
        setOrgInputValue(value || "");
      }
    },
    [resetOrgSearch, setOrgSearchText, selectedOrg],
  );

  const applyCatalogRowToCart = useCallback(
    (row, qtyInput) => {
      const productId = String(row.admin_product_id || row.id || "");
      const override = priceOverrides[productId];
      const rowWithPrices = override
        ? {
            ...row,
            unit_cost: override.unit_cost,
            unit_price: override.unit_price,
            needs_pricing: false,
          }
        : row;
      const result = addOrUpdateFromCatalogRow(rowWithPrices, qtyInput);
      if (!result.ok) {
        if (result.needsPricing) {
          setPendingAddQty(Math.max(1, Math.floor(Number(qtyInput)) || 1));
          setPriceDialogError(null);
          setPriceDialogProduct({
            sku: row.sku,
            product_name: row.product_name,
            admin_product_id: row.admin_product_id || row.id,
            unit_cost: row.unit_cost,
            unit_price: row.unit_price,
            inventory_id: row.inventory_id || row.id,
          });
          return;
        }
        setCartMessage(result.message);
        return;
      }
      setCartMessage(result.warning || "Added to order");
    },
    [addOrUpdateFromCatalogRow, priceOverrides],
  );

  const getPriceOverride = useCallback(
    (productId) => priceOverrides[productId] || null,
    [priceOverrides],
  );

  const handlePriceSelectOpen = useCallback((row) => {
    const productId = String(row.admin_product_id || row.id || "");
    setPriceSelectProduct({
      ...row,
      admin_product_id: productId,
    });
  }, []);

  const handlePriceSelectConfirm = useCallback(
    ({ unit_cost, unit_price }) => {
      if (!priceSelectProduct) return;
      const productId = String(
        priceSelectProduct.admin_product_id || priceSelectProduct.id || "",
      );
      setPriceOverrides((prev) => ({
        ...prev,
        [productId]: { unit_cost, unit_price },
      }));
      const cartLine = getLineForLot(productId);
      if (cartLine) {
        updateLinePricing(productId, {
          unit_price: unit_cost,
          mrp_price: unit_price,
        });
      }
      setPriceSelectProduct(null);
      setCartMessage("Price updated for this order.");
    },
    [priceSelectProduct, getLineForLot, updateLinePricing],
  );

  const handleAddProduct = useCallback(
    (row, qtyInput) => {
      applyCatalogRowToCart(row, qtyInput);
    },
    [applyCatalogRowToCart],
  );

  const applyResolveToCart = useCallback(
    (resolved, qtyInput) => {
      if (resolved.status === "error") {
        if (resolved.remediation?.make_purchasable || resolved.remediation?.add_pricing) {
          setSkuRemediation({
            remediation: resolved.remediation,
            sku: resolved.article_code,
            productName: resolved.product_name,
            adminProductId: resolved.admin_product_id,
            qty: Math.max(1, Math.floor(Number(qtyInput)) || 1),
          });
          setSkuResolveError(resolved.error_message || "This line cannot be added yet.");
          return;
        }
        setSkuRemediation(null);
        setSkuResolveError(null);
        setCartMessage(resolved.error_message || "Could not resolve SKU.");
        return;
      }

      setSkuRemediation(null);
      setSkuResolveError(null);
      const row = resolveResultToCatalogRow(resolved);
      const productId = String(row.admin_product_id || row.id || "");
      const override = priceOverrides[productId];
      const rowWithPrices = override
        ? {
            ...row,
            unit_cost: override.unit_cost,
            unit_price: override.unit_price,
            needs_pricing: false,
          }
        : row;
      const result = addOrUpdateFromCatalogRow(rowWithPrices, qtyInput);
      if (!result.ok) {
        if (result.needsPricing) {
          setPendingAddQty(Math.max(1, Math.floor(Number(qtyInput)) || 1));
          setPriceDialogError(null);
          setPriceDialogProduct({
            sku: row.sku,
            product_name: row.product_name,
            admin_product_id: row.admin_product_id,
            unit_cost: row.unit_cost,
            unit_price: row.unit_price,
            inventory_id: row.inventory_id,
          });
          return;
        }
        setCartMessage(result.message);
        return;
      }
      const warningMsg =
        result.warning ||
        (result.warnings?.length ? result.warnings.join(" ") : null);
      setCartMessage(warningMsg || "Added to order");
    },
    [addOrUpdateFromCatalogRow, priceOverrides],
  );

  const handleSkuLookup = useCallback(async () => {
    const sku = skuLookup.trim();
    if (!sku) {
      setCartMessage("Enter an article code / SKU to look up.");
      return;
    }
    if (!selectedOrg?.id) {
      setCartMessage("Select a shop first.");
      return;
    }
    setSkuLookupLoading(true);
    setPriceDialogError(null);
    setSkuResolveError(null);
    setSkuRemediation(null);
    try {
      const res = await purchaseCatalogService.resolveSku(selectedOrg.id, sku, 1);
      const resolved = res?.data || res;
      applyResolveToCart(resolved, 1);
    } catch (err) {
      const msg = err.response?.data?.message;
      setCartMessage(
        Array.isArray(msg)
          ? msg.join(", ")
          : msg || err.message || "Failed to look up SKU",
      );
    } finally {
      setSkuLookupLoading(false);
    }
  }, [skuLookup, selectedOrg, applyResolveToCart]);

  const handleSkuRemediationSuccess = useCallback(async () => {
    if (!skuRemediation?.sku || !selectedOrg?.id) return;
    setSkuLookupLoading(true);
    setSkuResolveError(null);
    try {
      const res = await purchaseCatalogService.resolveSku(
        selectedOrg.id,
        skuRemediation.sku,
        skuRemediation.qty || 1,
      );
      const resolved = res?.data || res;
      applyResolveToCart(resolved, skuRemediation.qty || 1);
      refetchCatalog?.();
    } catch (err) {
      const msg = err.response?.data?.message;
      setSkuResolveError(
        Array.isArray(msg)
          ? msg.join(", ")
          : msg || err.message || "Failed to re-resolve SKU",
      );
    } finally {
      setSkuLookupLoading(false);
    }
  }, [skuRemediation, selectedOrg, applyResolveToCart, refetchCatalog]);

  const handlePriceConfigSubmit = useCallback(
    async ({ unit_cost, unit_price, quantity_on_hand, location_code }) => {
      if (!priceDialogProduct?.sku || !selectedOrg?.id) return;
      setPriceDialogSaving(true);
      setPriceDialogError(null);
      try {
        if (priceDialogProduct.inventory_id) {
          await inventoryService.updateAdminInventoryByLotId(
            priceDialogProduct.inventory_id,
            {
              unit_cost,
              unit_price,
              reason: "Price config from Create Purchase Order",
            },
          );
        } else {
          await inventoryService.createOrUpdateInventory(priceDialogProduct.sku, {
            unit_cost,
            unit_price,
            quantity_on_hand,
            location_code,
            reason: "Price config from Create Purchase Order",
          });
          await ensureOrgMappedToLocationCode(
            selectedOrg.id,
            location_code || defaultPricingLocationCode,
          );
        }

        const res = await purchaseCatalogService.resolveSku(
          selectedOrg.id,
          priceDialogProduct.sku,
          pendingAddQty,
        );
        const resolved = res?.data || res;
        if (resolved.status === "error") {
          setPriceDialogError(
            resolved.error_message || "Could not resolve SKU after pricing.",
          );
          return;
        }

        applyResolveToCart(resolved, pendingAddQty);
        setPriceDialogProduct(null);
        refetchCatalog?.();
      } catch (err) {
        const msg = err.response?.data?.message;
        setPriceDialogError(
          Array.isArray(msg)
            ? msg.join(", ")
            : msg || err.message || "Failed to save price config",
        );
      } finally {
        setPriceDialogSaving(false);
      }
    },
    [
      priceDialogProduct,
      pendingAddQty,
      selectedOrg,
      defaultPricingLocationCode,
      applyResolveToCart,
      refetchCatalog,
    ],
  );

  const handleSubmit = useCallback(async () => {
    if (!selectedOrg?.id) {
      setSubmitError("Select a shop.");
      return;
    }
    const items = toSubmitItems();
    if (!items.length) {
      setSubmitError("Add at least one product.");
      return;
    }
    setSubmitting(true);
    setSubmitError(null);
    try {
      const res = await adminOrderService.createPurchaseOrder({
        org_id: selectedOrg.id,
        items,
        allow_insufficient_stock: true,
      });
      allowNavigation();
      clearCart();
      const orderId = res?.order_id;
      if (orderId) {
        navigate(`/orders/${orderId}`, {
          state: { ordersListPath: "/orders/purchase" },
        });
      } else {
        navigate("/orders/purchase");
      }
    } catch (err) {
      const msg = err.response?.data?.message;
      setSubmitError(
        Array.isArray(msg)
          ? msg.join(", ")
          : msg || err.message || "Failed to create order",
      );
    } finally {
      setSubmitting(false);
    }
  }, [selectedOrg, toSubmitItems, navigate, allowNavigation, clearCart]);

  const cartPanel = (
    <SelectedProductsPanel
      lines={cartLines}
      stockWarnings={stockWarnings}
      fulfillmentWarnings={fulfillmentWarnings}
      onUpdateQty={updateLineQty}
      onRemove={removeLine}
      lineCount={lineCount}
      estimatedSubtotal={estimatedSubtotal}
      onSubmit={handleSubmit}
      submitting={submitting}
      submitDisabled={!selectedOrg}
    />
  );

  return (
    <Box sx={{ pb: isMobile && cartLines.length > 0 ? 10 : 0 }}>
      {orgListError && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {orgListError}
        </Alert>
      )}

      <PurchaseOrderHeader
        onBack={() => tryNavigate("/orders/purchase")}
        organizations={organizationOptions}
        orgsLoading={orgsLoading}
        orgsLoadingMore={orgsLoadingMore}
        onOrgListScroll={handleListboxScroll}
        selectedOrg={selectedOrg}
        orgInputValue={orgInputValue}
        onOrgChange={handleOrgChange}
        onOrgInputChange={handleOrgInputChange}
        onUploadCsv={() => {
          if (!selectedOrg?.id) return;
          const orgName = encodeURIComponent(
            formatOrganizationLabel(selectedOrg),
          );
          navigate(
            `/orders/purchase/create/upload?orgId=${selectedOrg.id}&orgName=${orgName}`,
          );
        }}
        uploadCsvDisabled={!selectedOrg?.id}
      />

      {submitError && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          onClose={() => setSubmitError(null)}
        >
          {submitError}
        </Alert>
      )}

      {catalog.error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {catalog.error}
        </Alert>
      )}

      <ResponsiveLayout
        catalog={
          <Card sx={{ p: 2, boxShadow: "0 2px 8px rgba(0,0,0,0.08)" }}>
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 2,
                mb: 2,
                alignItems: "flex-start",
              }}
            >
              <ProductSearchBar
                value={catalog.searchInput}
                onChange={catalog.setSearchInput}
                disabled={!selectedOrg}
              />
              <ProductFilters
                category={catalog.category}
                onCategoryChange={catalog.setCategory}
                categories={catalog.categories}
                loading={catalog.categoriesLoading}
                disabled={!selectedOrg}
              />
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Add by article code (if not in catalog / missing price)
              </Typography>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                <TextField
                  size="small"
                  label="Article code / SKU"
                  value={skuLookup}
                  onChange={(e) => setSkuLookup(e.target.value)}
                  disabled={!selectedOrg || skuLookupLoading}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleSkuLookup();
                    }
                  }}
                  sx={{ minWidth: 220 }}
                />
                <Button
                  variant="outlined"
                  onClick={handleSkuLookup}
                  disabled={!selectedOrg || skuLookupLoading}
                >
                  {skuLookupLoading ? "Looking up…" : "Look up"}
                </Button>
              </Stack>
              {skuResolveError && (
                <Alert severity="error" sx={{ mt: 1.5 }}>
                  {skuResolveError}
                  {skuRemediation?.remediation ? (
                    <Box sx={{ mt: 1 }}>
                      <PurchaseOrderRemediateActions
                        mode="standalone"
                        orgId={selectedOrg?.id}
                        remediation={skuRemediation.remediation}
                        sku={skuRemediation.sku}
                        productName={skuRemediation.productName}
                        adminProductId={skuRemediation.adminProductId}
                        defaultLocationCode={
                          skuRemediation.remediation?.add_pricing?.location_code ||
                          defaultPricingLocationCode
                        }
                        disabled={skuLookupLoading}
                        onSuccess={handleSkuRemediationSuccess}
                        onError={setSkuResolveError}
                        stackDirection="row"
                      />
                    </Box>
                  ) : null}
                </Alert>
              )}
            </Box>

            {!selectedOrg ? (
              <Alert severity="info">
                Select a shop to browse the purchase catalog.
              </Alert>
            ) : (
              <ProductList
                rows={catalog.rows}
                loading={catalog.loading}
                page={catalog.page}
                rowsPerPage={catalog.rowsPerPage}
                total={catalog.total}
                onPageChange={catalog.handleChangePage}
                onRowsPerPageChange={catalog.handleChangeRowsPerPage}
                getLineForLot={getLineForLot}
                getPriceOverride={getPriceOverride}
                onAdd={handleAddProduct}
                onPriceConfig={handlePriceSelectOpen}
                disabled={!selectedOrg}
              />
            )}
          </Card>
        }
        cart={cartPanel}
        mobileCartTrigger={
          isMobile && cartLines.length > 0 ? (
            <>
              <Fab
                color="primary"
                onClick={() => setMobileCartOpen(true)}
                sx={{
                  position: "fixed",
                  bottom: 24,
                  right: 24,
                  bgcolor: "#e90000",
                  "&:hover": { bgcolor: "#c00000" },
                }}
              >
                <ShoppingCartIcon />
              </Fab>
              <SwipeableDrawer
                anchor="bottom"
                open={mobileCartOpen}
                onClose={() => setMobileCartOpen(false)}
                onOpen={() => setMobileCartOpen(true)}
                PaperProps={{
                  sx: {
                    borderTopLeftRadius: 16,
                    borderTopRightRadius: 16,
                    maxHeight: "85vh",
                    p: 2,
                  },
                }}
              >
                {cartPanel}
              </SwipeableDrawer>
            </>
          ) : null
        }
      />

      <PriceConfigDialog
        open={Boolean(priceDialogProduct)}
        product={priceDialogProduct}
        defaultLocationCode={defaultPricingLocationCode}
        submitting={priceDialogSaving}
        error={priceDialogError}
        onClose={() => {
          if (priceDialogSaving) return;
          setPriceDialogProduct(null);
          setPriceDialogError(null);
        }}
        onSubmit={handlePriceConfigSubmit}
      />

      <PurchaseOrderPriceSelectDialog
        open={Boolean(priceSelectProduct)}
        product={priceSelectProduct}
        initialPrices={
          priceSelectProduct
            ? priceOverrides[
                String(
                  priceSelectProduct.admin_product_id ||
                    priceSelectProduct.id ||
                    "",
                )
              ] || null
            : null
        }
        onClose={() => setPriceSelectProduct(null)}
        onConfirm={handlePriceSelectConfirm}
      />

      <Snackbar
        open={Boolean(cartMessage)}
        autoHideDuration={4000}
        onClose={() => setCartMessage(null)}
        message={cartMessage}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />

      <Dialog
        open={showLeaveDialog}
        onClose={cancelLeave}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600 }}>Discard order?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You have products in this order. If you leave this page or open
            another section, your cart will be discarded. Do you want to
            continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={cancelLeave} variant="outlined">
            Stay on page
          </Button>
          <Button
            onClick={confirmLeave}
            variant="contained"
            color="error"
            autoFocus
          >
            Leave and discard
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CreatePurchaseOrderPage;
